import React from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Divider,
  Button,
  Tooltip,
  FormControlLabel,
  useTheme,
} from '@material-ui/core';
import { MdTune } from 'react-icons/md';
import { LuRefreshCw } from 'react-icons/lu';
import { AppAccordion } from '@vyce/core/src/components/AppAccordion';
import { StyledBadge } from '@vyce/core/src/components/Dashboard/ExpandedMenuButton';
import { AppCheckbox } from '@vyce/core/src/components/inputs';

import { MAIN_CONTAINER_ID } from '../../constants';

import { ButtonTitleWithLoading } from '../ButtonTitleWithLoading';

import { useStyles } from './styles';
import { Props } from './types';
import { WidgetCheckboxItem } from './components/WidgetCheckboxItem';
import { CheckboxItem } from './components/CheckboxItem';
import { useData } from './hooks/useData';
import { ConfirmDialog } from '../ConfirmDialog';

export const CustomizeDashboard: React.FC<Props> = props => {
  const classes = useStyles();
  const theme = useTheme();

  const {
    isMobile,
    open,
    showRestoreButton,
    accordionName,
    candidatesToUpdate,
    selectedLocationsCount,
    notAllLocationsSelected,
    allLocationsSelected,
    locationList,
    selectedTeamsCount,
    notAllTeamsSelected,
    allTeamsSelected,
    teamList,
    loading,
    isOpenConfirmDialog,
    setConfirmDialogOpen,
    setConfirmDialogClose,
    handleTeamChange,
    handleSelectAllTeamsChange,
    handleUpdateClick,
    handleLocationChange,
    handleSelectAllLocationsChange,
    onCandidateToUpdateChange,
    handleAccordionChange,
    handleResetClick,
    handleClose,
    setOpenDialog,
  } = useData(props);

  const tooltipTitle = [
    props.timePermission && locationList.length > 0
      ? `${selectedLocationsCount}/${locationList.length} locations selected`
      : '',
    props.teamPermission && teamList.length > 0
      ? `${selectedTeamsCount}/${teamList.length} teams selected`
      : '',
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <>
      <Tooltip title={tooltipTitle}>
        <StyledBadge
          color="primary"
          variant="dot"
          overlap="rectangular"
          invisible={!notAllLocationsSelected && !notAllTeamsSelected}>
          <Button
            onClick={setOpenDialog}
            size="small"
            style={{ backgroundColor: theme.palette.background.paper }}
            startIcon={<MdTune />}
            variant="outlined"
            color="primary">
            Customise your dashboard
          </Button>
        </StyledBadge>
      </Tooltip>

      <Dialog
        fullScreen={isMobile}
        open={open}
        container={document.getElementById(MAIN_CONTAINER_ID)}
        onClose={handleClose}
        className={classes.dialog}
        aria-labelledby="responsive-dialog-title">
        <Box display="flex" alignItems="center" justifyContent="space-between" gridGap={8} height="60px">
          <DialogTitle id="responsive-dialog-title">Customise your dashboard!</DialogTitle>
          {showRestoreButton ? (
            <Box padding="32px 32px 0">
              <Button
                onClick={setConfirmDialogOpen}
                size="small"
                endIcon={<LuRefreshCw />}
                variant="outlined"
                color="primary">
                Restore to default
              </Button>
            </Box>
          ) : null}
        </Box>

        <DialogContent className={classes.dialogContent}>
          <AppAccordion
            title="Which widgets should be displayed?"
            noPadding
            outsideControl
            outsideExpanded={accordionName === 'windows'}
            panel="windows"
            onOutsideChange={handleAccordionChange}
            renderDetailsBlock={() => (
              <Box marginLeft="-5px" overflow="auto" maxHeight="calc(100vh - 315px)" height="max-content">
                {candidatesToUpdate.map(item => (
                  <WidgetCheckboxItem
                    key={item.system_name}
                    {...item}
                    onChange={onCandidateToUpdateChange}
                  />
                ))}
              </Box>
            )}
          />
          {props.timePermission && (
            <>
              <Divider />
              <AppAccordion
                title="Which Locations should be included?"
                noPadding
                outsideControl
                outsideExpanded={accordionName === 'locations'}
                panel="locations"
                onOutsideChange={handleAccordionChange}
                extraSummaryText={
                  selectedLocationsCount === locationList.length
                    ? ''
                    : `${selectedLocationsCount}/${locationList.length} selected`
                }
                renderDetailsBlock={() => (
                  <>
                    <Typography variant="caption">
                      This filter will be applied to all relevant widgets.
                    </Typography>
                    <Box
                      marginLeft="-5px"
                      overflow="auto"
                      maxHeight="calc(100vh - 470px)"
                      height="max-content">
                      {locationList.length > 0 && (
                        <Box
                          marginTop="12px"
                          borderBottom={`1px solid ${theme.palette.divider}`}
                          paddingBottom="12px"
                          marginBottom="12px">
                          <FormControlLabel
                            classes={{ label: classes.label }}
                            control={
                              <AppCheckbox
                                variant="rectangle"
                                color="primary"
                                checked={allLocationsSelected}
                                onChange={handleSelectAllLocationsChange}
                              />
                            }
                            label="Select All"
                          />
                        </Box>
                      )}
                      {locationList.map(item => (
                        <CheckboxItem key={item.uuid} {...item} onChange={handleLocationChange} />
                      ))}
                    </Box>
                  </>
                )}
              />
            </>
          )}
          {props.teamPermission && (
            <>
              <Divider />
              <AppAccordion
                title="Which Teams should be included?"
                outsideControl
                outsideExpanded={accordionName === 'teams'}
                panel="teams"
                onOutsideChange={handleAccordionChange}
                extraSummaryText={
                  selectedTeamsCount === teamList.length
                    ? ''
                    : `${selectedTeamsCount}/${teamList.length} selected`
                }
                noPadding
                renderDetailsBlock={() => (
                  <>
                    <Typography variant="caption">
                      This filter will be applied to all relevant widgets.
                    </Typography>
                    <Box
                      marginLeft="-5px"
                      overflow="auto"
                      maxHeight="calc(100vh - 470px)"
                      height="max-content">
                      {teamList.length > 0 && (
                        <Box
                          marginTop="12px"
                          borderBottom={`1px solid ${theme.palette.divider}`}
                          paddingBottom="12px"
                          marginBottom="12px">
                          <FormControlLabel
                            classes={{ label: classes.label }}
                            control={
                              <AppCheckbox
                                variant="rectangle"
                                color="primary"
                                checked={allTeamsSelected}
                                onChange={handleSelectAllTeamsChange}
                              />
                            }
                            label="Select All"
                          />
                        </Box>
                      )}
                      {teamList.map(item => (
                        <CheckboxItem key={item.uuid} {...item} onChange={handleTeamChange} />
                      ))}
                    </Box>
                  </>
                )}
              />
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button size="large" onClick={handleClose} variant="outlined">
            Close
          </Button>
          <Button
            size="large"
            color="primary"
            onClick={handleUpdateClick}
            variant="contained"
            className={classes.submitButton}
            disabled={!showRestoreButton}>
            <ButtonTitleWithLoading title="Update" loaderVariant="paper" loading={loading} />
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        handleClose={setConfirmDialogClose}
        open={isOpenConfirmDialog}
        confirmText="Yes"
        cancelText="No"
        title="Are you sure you want to restore to default?"
        handleConfirm={handleResetClick}
      />
    </>
  );
};
